/**
 * @design-contract CUR-113 + CUR-206
 *
 * Stream-phase policy for the chat surface. Defines the rules for:
 *   - When the assistant's response text is still "revealing" (so the
 *     reveal animation should keep playing and runway anchors stay live).
 *   - When a PMV cluster counts as "streaming" vs "completed" for the
 *     purpose of rendering shimmer / pulse / "leave open" hints.
 *
 * Mirrors `common_lib.stream_phase_policy` on the backend at a high
 * level; the two layers cooperate but enforce different invariants.
 *
 * INSTRUCTIONS FOR HUMAN OR AI AGENTS EDITING THIS FILE:
 *   - These predicates are PURE. Do not introduce React state, hooks,
 *     or side effects.
 *   - DO NOT loosen `canContinueReveal` to "is the message text" — the
 *     `continueStreamingAnimation` flag exists specifically to keep
 *     completed turns from re-animating when a user refetches the
 *     conversation after a refresh.
 *   - DO NOT change the "interleaved => last text segment is the active
 *     response" rule. See `chatClusterSelectors.ts:getActiveResponseTextMessageIndex`
 *     for the corresponding DO NOT REVERT.
 */

import type { ChatMessage } from '../chatTypes'

/**
 * Whether the active response can keep playing its reveal animation.
 *
 * True iff the message is a text type AND either (a) the conversation
 * is still loading (we expect more text deltas to arrive), or (b) the
 * message itself carries `continueStreamingAnimation` (e.g. handing off
 * across an interleaved-turn boundary where the loading flag may flip
 * briefly).
 *
 * INVARIANT: must remain a PURE function of the inputs. Used inside
 * `useMemo` deps; impure behavior here will cause infinite re-renders.
 */
export function canContinueReveal(
  activeResponseTextMessage: ChatMessage | null | undefined,
  isLoading: boolean
): boolean {
  if (!activeResponseTextMessage) return false
  if (activeResponseTextMessage.type !== 'text') return false
  return isLoading || !!activeResponseTextMessage.continueStreamingAnimation
}

/**
 * Whether `progress_message` cluster `messageId` should be considered
 * "streaming/active" vs "completed". The rule mirrors the backend's
 * phase-boundary semantics: the active cluster is whichever last
 * progress entry has no user message after it AND the turn is still
 * loading OR being revealed.
 *
 * Pure: no DOM access, no hooks.
 */
export function isProgressClusterStreaming(args: {
  isLastProgressInArray: boolean
  hasUserMessageAfter: boolean
  isLoading: boolean
}): boolean {
  if (!args.isLastProgressInArray) return false
  if (args.hasUserMessageAfter) return false
  return args.isLoading
}
